/**
 * Where you stand with everyone.
 *
 * Readable at a glance is the whole point — this is a thing you check before
 * committing to a jump, not a spreadsheet you study. So each faction is a row:
 * its mark, its name, a bar, and what that number currently gets you.
 *
 * The thresholds are the design doc's, and they are shown as marks on the bar
 * rather than as a legend somewhere else. Knowing you are eight points off
 * *trusted* is the thing that makes a rank feel worth pushing.
 */

import {
  ALIGNABLE_FACTION_IDS,
  FACTIONS,
  shownStanding,
  type FactionId,
  type FactionStanding,
} from '@interspacial/shared';

import { factionIconUrl } from './factionIcons';

/** Rank bands, from the design doc. Standing runs −10 to +100. */
const BANDS = [
  { at: -5, name: 'hunted', color: '#ff2d2d' },
  { at: 0, name: 'hostile', color: '#ff5f6b' },
  { at: 10, name: 'neutral', color: '#8b93a3' },
  { at: 25, name: 'allied', color: '#7fb46f' },
  { at: 50, name: 'trusted', color: '#7fd4ff' },
  { at: 75, name: 'honoured', color: '#ffd479' },
] as const;

const MIN_STANDING = -10;
const MAX_STANDING = 100;

function bandFor(standing: number) {
  return [...BANDS].reverse().find((band) => standing >= band.at) ?? BANDS[0];
}

export interface ReputationBoardProps {
  /** Missing entries read as the starting standing rather than as zero. */
  standing: FactionStanding;
  /** Whose station this is, so the one that matters is obvious. */
  host?: FactionId | null;
}

/** Everyone starts here, per the design doc. */
const STARTING_STANDING = 10;

export function ReputationBoard({ standing, host }: ReputationBoardProps) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {ALIGNABLE_FACTION_IDS.map((id) => {
        const faction = FACTIONS[id];
        const value = standing[id] ?? STARTING_STANDING;
        const band = bandFor(value);

        // Turquoise has no standing at all — it is neutral to everyone by rule,
        // and showing it an empty bar would imply a number that can move.
        const ranked = !faction.alwaysNeutral;

        return (
          <div key={id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <img
              src={factionIconUrl(id)}
              alt=""
              width={30}
              height={30}
              style={{ flexShrink: 0, imageRendering: 'auto' }}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: host === id ? '#ffd479' : '#dbeaff' }}>
                  {faction.name}
                  {host === id && <span style={{ opacity: 0.55 }}> · this station</span>}
                </span>
                <span style={{ color: ranked ? band.color : 'rgba(219,234,255,0.5)' }}>
                  {/* Rounded here and nowhere else. Standing is continuous —
                      an ally's share of a short run is six tenths of a point —
                      and `band` above is decided on the true value, so somebody
                      on 24.7 reads as 25 and is correctly not yet allied. */}
                  {ranked ? `${shownStanding(value)} · ${band.name}` : 'always neutral'}
                </span>
              </div>

              {ranked && (
                <div
                  style={{
                    position: 'relative',
                    height: 5,
                    marginTop: 4,
                    background: 'rgba(255,255,255,0.07)',
                  }}
                >
                  {/* Thresholds, marked on the bar itself — being eight points
                      off the next band is what makes a rank worth pushing. */}
                  {BANDS.filter((b) => b.at > MIN_STANDING).map((b) => (
                    <div
                      key={b.at}
                      style={{
                        position: 'absolute',
                        left: `${((b.at - MIN_STANDING) / (MAX_STANDING - MIN_STANDING)) * 100}%`,
                        top: -1,
                        width: 1,
                        height: 7,
                        background: 'rgba(255,255,255,0.22)',
                      }}
                    />
                  ))}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: `${((Math.max(value, MIN_STANDING) - MIN_STANDING) / (MAX_STANDING - MIN_STANDING)) * 100}%`,
                      background: band.color,
                      opacity: 0.85,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 10, opacity: 0.4, lineHeight: 1.5, marginTop: 2 }}>
        Missions raise the faction you fly for and its ally, and cost you with
        its enemy. Below zero its stations close to you; below −5 they shoot.
      </div>
    </div>
  );
}
